import type { BillingType } from './billingType';
import { stripJobPrefix } from './dealLabel';

// Week-at-a-glance helpers. Week boundaries and day bucketing are all in
// America/Toronto to match how pay periods and the approval surface render —
// an engineer logging from another timezone still sees Capstone's week.
//
// Boundaries are computed as Toronto-local calendar dates ('YYYY-MM-DD') and
// bucketing maps each entry's instant to its Toronto day. We avoid a tz
// library: dates are kept as strings for arithmetic, and instants are mapped
// to a Toronto day via Intl. The fetch window is padded ±1 day so the
// browser/Toronto offset can't drop an edge entry; buildDealRows then filters
// to the exact 7 day-keys.

const TZ = 'America/Toronto';

const dayKeyFmt = new Intl.DateTimeFormat('en-CA', {
  timeZone: TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

// en-CA renders as YYYY-MM-DD, which is exactly the key shape we want.
export function torontoDayKey(iso: string | Date): string {
  return dayKeyFmt.format(typeof iso === 'string' ? new Date(iso) : iso);
}

// Parse 'YYYY-MM-DD' into a local Date at midnight, purely for day arithmetic
// (getDay / add-days). Never used for tz conversion.
function parseKey(key: string): Date {
  const p = key.split('-');
  return new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]));
}

function toKey(d: Date): string {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Saturday that starts the week containing `dayKey`. Weeks run Sat → Fri
// (Tim, 2026-07-12): the weekend leads the week so weekend work counts
// toward the upcoming week's hours — employees can work a weekend and take
// a weekday off later in that same week and still make their 40.
export function weekStartKey(dayKey: string): string {
  const d = parseKey(dayKey);
  const offset = (d.getDay() + 1) % 7; // Sat=6 -> 0, Sun=0 -> 1, Fri=5 -> 6
  d.setDate(d.getDate() - offset);
  return toKey(d);
}

export function addWeeks(weekStart: string, n: number): string {
  const d = parseKey(weekStart);
  d.setDate(d.getDate() + n * 7);
  return toKey(d);
}

// ISO instant `days` before/after a 'YYYY-MM-DD' key's local midnight — used
// to build the padded fetch window.
export function keyToIsoOffset(key: string, days: number): string {
  const d = parseKey(key);
  d.setDate(d.getDate() + days);
  return d.toISOString();
}

// Noon (local) on a day-key. Quick-add stamps new entries here so the entry
// lands on the intended Toronto calendar day regardless of browser tz.
export function keyToNoonIso(key: string): string {
  const d = parseKey(key);
  d.setHours(12, 0, 0, 0);
  return d.toISOString();
}

export interface WeekDay {
  key: string;
  weekday: string; // "Mon"
  dayNum: number; // 1..31
  isToday: boolean;
}

const weekdayFmt = new Intl.DateTimeFormat('en-CA', { weekday: 'short' });

export function weekDays(weekStart: string, todayKey: string): WeekDay[] {
  const start = parseKey(weekStart);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    const key = toKey(d);
    return { key, weekday: weekdayFmt.format(d), dayNum: d.getDate(), isToday: key === todayKey };
  });
}

export interface WeekEntryNote {
  id: string;
  note: string;
  createdAt: string;
}

export interface WeekEntry {
  id: string;
  dealId: string;
  projectId: string | null;
  startedAt: string;
  minutes: number;
  note: string | null;
  notes: WeekEntryNote[];
  billingType: BillingType;
  lockedAt: string | null;
  deal: { jobNumber: string | null; title: string } | null;
}

export interface DealRow {
  dealId: string;
  label: string;
  cells: Map<string, WeekEntry[]>; // dayKey -> entries that day
  totalMinutes: number;
}

// Tim's trial feedback: rows must show the deal name even without a job
// number, and HubSpot dealnames that repeat the job number ("9998 - …")
// shouldn't render the number twice.
function dealRowLabel(e: WeekEntry): string {
  if (!e.deal) return '— (deal missing)';
  const jobNumber = e.deal.jobNumber;
  const name = stripJobPrefix(jobNumber, e.deal.title);
  if (jobNumber && name) return `${jobNumber} · ${name}`;
  if (jobNumber) return jobNumber;
  return name || '— (unnamed deal)';
}

// Group entries into deal rows, bucketed by Toronto day, keeping only the
// seven days of the visible week. Rows sort by total minutes desc.
export function buildDealRows(entries: WeekEntry[], dayKeys: string[]): DealRow[] {
  const keySet = new Set(dayKeys);
  const byDeal = new Map<string, DealRow>();
  for (const e of entries) {
    const dayKey = torontoDayKey(e.startedAt);
    if (!keySet.has(dayKey)) continue;
    let row = byDeal.get(e.dealId);
    if (!row) {
      row = { dealId: e.dealId, label: dealRowLabel(e), cells: new Map(), totalMinutes: 0 };
      byDeal.set(e.dealId, row);
    }
    const list = row.cells.get(dayKey) ?? [];
    list.push(e);
    row.cells.set(dayKey, list);
    row.totalMinutes += e.minutes;
  }
  return [...byDeal.values()].sort(
    (a, b) => b.totalMinutes - a.totalMinutes || a.label.localeCompare(b.label),
  );
}

export interface CellSummary {
  hourlyMinutes: number;
  flatCountsByType: Map<BillingType, number>;
  entryCount: number;
  hasLocked: boolean;
}

// Split a cell's entries into hourly minutes vs per-type flat unit counts,
// using the caller's flat-type set (same flat/hourly decision the rest of the
// app makes from me.flatBillingTypes).
export function summarizeCell(entries: WeekEntry[], flatTypes: Set<string>): CellSummary {
  let hourlyMinutes = 0;
  const flatCountsByType = new Map<BillingType, number>();
  let hasLocked = false;
  for (const e of entries) {
    if (e.lockedAt) hasLocked = true;
    if (flatTypes.has(e.billingType)) {
      flatCountsByType.set(e.billingType, (flatCountsByType.get(e.billingType) ?? 0) + 1);
    } else {
      hourlyMinutes += e.minutes;
    }
  }
  return { hourlyMinutes, flatCountsByType, entryCount: entries.length, hasLocked };
}

// Two decimals so quarter-hour entries show exactly (2.25h, not 2.3h),
// with trailing zeros trimmed (8h, 2.5h) to avoid "8.00h" noise.
export function formatHours(minutes: number): string {
  return `${parseFloat((minutes / 60).toFixed(2))}h`;
}

const rangeFmt = new Intl.DateTimeFormat('en-CA', {
  timeZone: TZ,
  month: 'short',
  day: 'numeric',
});

// "Apr 6 – Apr 12" label for a week, from its Monday key.
export function weekRangeLabel(weekStart: string): string {
  const start = parseKey(weekStart);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return `${rangeFmt.format(start)} – ${rangeFmt.format(end)}`;
}

const dayLabelFmt = new Intl.DateTimeFormat('en-CA', {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
});

// "Mon, Jun 9" for a 'YYYY-MM-DD' key.
export function dayLabel(key: string): string {
  return dayLabelFmt.format(parseKey(key));
}
